/* eslint-disable @typescript-eslint/require-array-sort-compare */
import React, { useState, useCallback, useEffect, Fragment, ChangeEvent } from 'react';
import _get from 'lodash/get';
import type { UserContext } from '@zola-helpers/client/dist/es/@types';
import {
  getLocalStorage,
  setLocalStorage,
  deleteLocalStorage,
} from '@zola-helpers/client/dist/es/util/storage';
import { ENABLE_ONE_TIME_FEATURES } from '@zola-helpers/client/dist/es/util/userSettings';

import {
  experimentStates,
  setExperimentOverrides,
  getExperimentOverrides,
} from '@zola-helpers/client/dist/es/experiments';

import {
  setFeatureFlagOverride,
  getFeatureFlagOverrides,
} from '@zola-helpers/client/dist/es/featureFlags';

import NAV_DATA, {
  FeatureFlagsAvailableEventPayload,
  FeatureFlagsAvailableFlagSettings,
} from '@zola-helpers/client/dist/es/constants/navConstants';

import useKeyboardShortcut from '../../hooks/useKeyboardShortcut';
import CheckBoxField from '../Form/CheckboxField/CheckboxField';
import InputFieldV2 from '../Form/InputFieldV2/InputFieldV2';
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore File not yet converted to TS
import DropdownV2 from '../Form/Dropdown/DropdownV2';
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore File not yet converted to TS
import ButtonV2 from '../Button/ButtonV2';

import styles from './FeatureToggle.module.less';

type FeatureFlagOption = { value: string; label: string };

const {
  EVENTS: { FEATURE_FLAGS_AVAILABLE },
} = NAV_DATA;

interface FeatureToggleProps {
  userContext: UserContext;
  productFlags: FeatureFlagsAvailableFlagSettings;
  product: string;
}

export const FeatureToggle = ({
  userContext,
  productFlags,
  product,
}: FeatureToggleProps): JSX.Element => {
  const [experiment, setExperiment] = useState<string | null>(null);
  const [overrideVariation, setOverrideVariation] = useState<string | null>(null);
  const [showDropdown, setShowDropdown] = useState(false);
  const [featureFlag, setFeatureFlag] = useState('');
  const [featureFlagValue, setFeatureFlagValue] = useState<boolean | null>(null);
  const [overrideUserSettings, setOverrideUserSettings] = useState(
    Boolean(getLocalStorage(ENABLE_ONE_TIME_FEATURES))
  );

  /** If true, exclude feature flags that have been enabled in production from the drop down */
  const [excludeProductionFlags, setExcludeProductionFlags] = useState(true);

  /**
   * A lookup of domains (products) to feature flag information (value: booleam, production:
   * boolean, staging: boolean, development: boolean)  that gives us the flexibility to add
   * tickets or descriptions to the information in the future for better display / usefulness with qa
   */
  const [featureFlagsByDomain, setFeatureFlagsByDomain] = useState<{
    [domain: string]: FeatureFlagsAvailableFlagSettings;
  }>({});

  useEffect(() => {
    if (!Object.keys(featureFlagsByDomain).includes(product)) {
      setFeatureFlagsByDomain({
        ...featureFlagsByDomain,
        [product]: productFlags,
      });
    }
  }, [productFlags, product, featureFlagsByDomain]);

  /**
   * label/value paris for the feature flag drop down
   */
  const [featureFlagOptions, setFeatureFlagOptions] = useState<FeatureFlagOption[]>([]);

  const handleKeyboardShortcut = useCallback(() => {
    setShowDropdown((currentShowDropdown) => !currentShowDropdown);
  }, [setShowDropdown]);
  const keys = ['Control', 'Shift', 'J'];
  useKeyboardShortcut(keys, handleKeyboardShortcut);

  const experiments = userContext?.user_experiments;
  const sortedExperimentKeys = Object.keys(experiments || {}).sort(
    (a, b) => a.toLowerCase().localeCompare(b.toLowerCase()) // Case-insensitive sort
  );
  const getExperimentOptions = () => {
    return sortedExperimentKeys.map((e) => ({ label: e, value: e }));
  };

  const submitExperimentOverride = () => {
    if (!experiment || !overrideVariation) return; // TODO: add validation to dropdown
    setExperimentOverrides(experiment, overrideVariation);

    window.location.reload();
  };

  const handleUserSettingsOverrideClick = (e: ChangeEvent<HTMLInputElement>) => {
    setOverrideUserSettings(e.target.checked);
    if (e.target.checked) setLocalStorage(ENABLE_ONE_TIME_FEATURES, 'true');
    else deleteLocalStorage(ENABLE_ONE_TIME_FEATURES);

    window.location.reload();
  };

  const renderExperimentValueTable = () => {
    const storedOverrides = getExperimentOverrides();
    return (
      <div className={styles.tableContainer}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>Experiment</th>
              <th>Assigned Variation</th>
              <th>Override Variation</th>
            </tr>
          </thead>
          <tbody>
            {sortedExperimentKeys.map((k) => (
              <tr key={k}>
                <th>{k}</th>
                <th>{experiments?.[k].variation_key}</th>
                <th>{_get(storedOverrides, k, '--')}</th>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  const submitFeatureFlagOverride = () => {
    if (!featureFlag || featureFlagValue === null) {
      return;
    }

    setFeatureFlagOverride(featureFlag, featureFlagValue);
    window.location.reload();
  };

  const featureFlagOverrides = getFeatureFlagOverrides();

  /**
   * Listerner for the domains (products) firing a FEATURE_FLAGS_AVAILABLE
   * event that provides the list of feature flags for the product
   */
  useEffect(() => {
    const handleFeatureFlagsLoaded = (event: CustomEvent<FeatureFlagsAvailableEventPayload>) => {
      const {
        detail: { domain, flags },
      } = event;

      const newFlagsByDomain = {
        ...featureFlagsByDomain,
        [domain]: flags,
      };

      setFeatureFlagsByDomain(newFlagsByDomain);
    };

    window.addEventListener(FEATURE_FLAGS_AVAILABLE, handleFeatureFlagsLoaded as EventListener);
    return () => {
      window.removeEventListener(
        FEATURE_FLAGS_AVAILABLE,
        handleFeatureFlagsLoaded as EventListener
      );
    };
  }, [featureFlagsByDomain]);

  /**
   * Whenever we get new feature flags, compute the drop down options
   */
  useEffect(() => {
    const domains = Object.keys(featureFlagsByDomain).sort();

    let options: FeatureFlagOption[] = [];

    domains.forEach((domain) => {
      const flagsForDomain = featureFlagsByDomain[domain];

      const optionsForDomain = Object.keys(flagsForDomain)
        // filter out (or not) feature flgas that are on in production
        .filter((flag) => (excludeProductionFlags ? !flagsForDomain[flag].production : true))
        .sort()
        .map((flag) => {
          return {
            value: flag,
            // This is the text display for the dropdown
            label: `${flag} (${flagsForDomain[flag].value}) - ${domain}`,
          };
        });

      options = [...options, ...optionsForDomain];
    });

    setFeatureFlagOptions(options);
  }, [featureFlagsByDomain, excludeProductionFlags]);

  return showDropdown ? (
    <div className={styles.container}>
      <div className={styles.flex}>
        <div className={styles.experiments}>
          <DropdownV2
            id="experimentList"
            options={getExperimentOptions()}
            label="Experiment"
            onSelect={setExperiment}
            value={experiment}
            className={styles.dropdown}
          />
          <div>
            <span className={styles.bold}>Assigned User Variation: </span>
            {_get(userContext, `user_experiments.${experiment}.variation_key`, 'N/A')}
          </div>
          <div>
            <span className={styles.bold}>Current Override Variation: </span>
            {_get(getExperimentOverrides(), `${experiment}`, 'not set')}
          </div>
          <DropdownV2
            id="experimentOverrideVariation"
            options={[
              { label: experimentStates.NOT_IN_TEST, value: experimentStates.NOT_IN_TEST },
              { label: experimentStates.CONTROL, value: experimentStates.CONTROL },
              { label: 'TEST-1', value: 'TEST-1' },
              { label: 'TEST-2', value: 'TEST-2' },
              { label: 'TEST-3', value: 'TEST-3' },
              { label: 'TEST-4', value: 'TEST-4' },
            ]}
            label="Set Override Variation"
            onSelect={setOverrideVariation}
            value={overrideVariation}
            className={styles.dropdown}
            inline
          />
          <ButtonV2 onClick={submitExperimentOverride}>Override Variation And Reload</ButtonV2>
        </div>
        <div className={styles.featureFlags}>
          {Object.keys(featureFlagsByDomain).length === 0 ? (
            <InputFieldV2
              type="text"
              label="Feature Flag"
              value={featureFlag}
              onChange={(e: ChangeEvent<HTMLInputElement>) => setFeatureFlag(e.target.value)}
            />
          ) : (
            <>
              <DropdownV2
                id="featureFlagList"
                options={featureFlagOptions}
                label="Feature Flag"
                className={styles.dropdown}
                value={featureFlag}
                onSelect={setFeatureFlag}
                inline
              />
              <CheckBoxField
                id="excludeProductionFlags"
                inline
                label="Exclude production flags"
                onChange={(e: ChangeEvent<HTMLInputElement>) =>
                  setExcludeProductionFlags(e.target.checked)
                }
                checked={excludeProductionFlags}
              />
              <hr />
            </>
          )}
          <DropdownV2
            id="featureFlagValue"
            options={[
              { label: 'True', value: true },
              { label: 'False', value: false },
            ]}
            label="Value"
            onSelect={setFeatureFlagValue}
            value={featureFlagValue}
            nullable
          />
          <div>
            <label>Current Overrides</label>
            <ul>
              {Object.keys(featureFlagOverrides).map((key) => (
                <li key={key}>
                  {key}: {JSON.stringify(featureFlagOverrides[key])}
                </li>
              ))}
            </ul>
          </div>
          <ButtonV2 onClick={submitFeatureFlagOverride}>
            Override Feature Flag Override And Reload
          </ButtonV2>
        </div>
        {renderExperimentValueTable()}
      </div>

      <CheckBoxField
        className={styles.checkbox}
        id="disableUserSettings"
        inline
        label="Enable all one-time features (e.g. tours/modals)"
        onChange={handleUserSettingsOverrideClick}
        checked={overrideUserSettings}
      />
    </div>
  ) : (
    <Fragment />
  );
};

export default FeatureToggle;
